/**
 * Built-in shell message catalogs (见 14 / §5.11). Authored as JSON under
 * `./nls/<locale>.json` (same shape as theme palettes): key sets are open
 * strings, completeness is a data concern. Module strings live in each
 * module's own `nls/<locale>.json`.
 *
 * Values are ICU MessageFormat strings. Catalogs along
 * {@link localeLookupChain} are merged at runtime, so a locale may omit keys.
 */
import enUS from './nls/en-US.json';
import esES from './nls/es-ES.json';
import filPH from './nls/fil-PH.json';
import frFR from './nls/fr-FR.json';
import hiIN from './nls/hi-IN.json';
import idID from './nls/id-ID.json';
import itIT from './nls/it-IT.json';
import jaJP from './nls/ja-JP.json';
import kmKH from './nls/km-KH.json';
import koKR from './nls/ko-KR.json';
import msMY from './nls/ms-MY.json';
import ptBR from './nls/pt-BR.json';
import ruRU from './nls/ru-RU.json';
import viVN from './nls/vi-VN.json';
import zhCN from './nls/zh-CN.json';
import zhTW from './nls/zh-TW.json';
import { BUILTIN_LOCALES, type MessageCatalog } from './nls-contract';

const SHELL_BY_LOCALE: Record<string, MessageCatalog> = {
  'en-US': enUS,
  'zh-CN': zhCN,
  'zh-TW': zhTW,
  'ru-RU': ruRU,
  'fr-FR': frFR,
  'es-ES': esES,
  'it-IT': itIT,
  'pt-BR': ptBR,
  'vi-VN': viVN,
  'hi-IN': hiIN,
  'fil-PH': filPH,
  'id-ID': idID,
  'ja-JP': jaJP,
  'ko-KR': koKR,
  'ms-MY': msMY,
  'km-KH': kmKH,
};

/** Built-in English (US) shell catalog — first-paint seed for the renderer. */
export const EN_US_SHELL: MessageCatalog = enUS;

/** Built-in Simplified Chinese shell catalog. */
export const ZH_CN_SHELL: MessageCatalog = zhCN;

/** Built-in shell catalogs keyed by locale, in {@link BUILTIN_LOCALES} order. */
export const BUILTIN_SHELL_CATALOGS: Record<string, MessageCatalog> =
  Object.fromEntries(
    BUILTIN_LOCALES.map((locale) => {
      const catalog = SHELL_BY_LOCALE[locale];
      if (!catalog) {
        throw new Error(`missing built-in shell catalog for ${locale}`);
      }
      return [locale, catalog];
    }),
  );
